import { APP_CONFIG } from "../../init";
import { APIClient } from "../../utilities/APIClient";
import { DateTime } from "luxon";
import { contentPageResponse } from "../commonTypes";
import {
  ExternalResource,
  ExternalResourceDTO,
  ExternalResourceLibrary,
} from "./ExternalResource";
import { handleErrors } from "../../app/APIError";
import { dirbatLocale } from "../../translations/translations";

function getExternalResources(data: []): ExternalResource[] {
  return data.map((nd: any) => mapExternalResourceFromApi(nd));
}

function mapExternalResourceFromApi(rd: any): ExternalResource {
  return {
    headline: rd.headline,
    id: rd.id,
    triggersNotification: rd.triggersNotification,
    photo: rd.imageUrl,
    publicationDate: DateTime.fromISO(rd.publishedAt),
    removalDate: DateTime.fromISO(rd.unpublishedAt),
    status: rd.status,
    title: rd.title,
    url: rd.url,
  };
}

export class ExternalResourceLibraryAPI
  extends APIClient
  implements ExternalResourceLibrary {
  constructor(
    token: string,
    clientId: string,
    private supplierId: string,
    language: dirbatLocale
  ) {
    super(token, language);
  }

  findPaginated(
    firstIdx: number,
    itemsPerPage: number
  ): Promise<contentPageResponse<ExternalResource>> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${
        this.supplierId
      }/external_resources?page=${Math.floor(firstIdx / itemsPerPage) + 1}`
    )
      .then((response) => response.json())
      .then((data) => {
        return {
          items: getExternalResources(data["hydra:member"]),
          stats: { totalCount: data["hydra:totalItems"] },
        };
      });
  }

  findOneById(id: string): Promise<ExternalResource> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/external_resources/${id}`
    )
      .then((response) => response.json())
      .then((data): ExternalResource => mapExternalResourceFromApi(data));
  }

  delete(resource: ExternalResource): Promise<boolean> {
    return this.fetch(
      `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/external_resources/${resource.id}`,
      { method: "delete" }
    ).then((response) => response.status === 204);
  }

  save(dto: ExternalResourceDTO): Promise<ExternalResource> {
    const method = dto.image ? "POST" : "PATCH";
    const endPointUrl = dto.id
      ? `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/external_resources/${dto.id}`
      : `${APP_CONFIG.API_BASE_URI}/suppliers/${this.supplierId}/external_resources`;
    let contentType;
    let body;
    if (dto.image === undefined) {
      contentType = "application/merge-patch+json";
      body = JSON.stringify({
        url: dto.url,
        title: dto.title,
        headline: dto.headline,
        triggersNotification: dto.triggersNotification,
        publishedAt: dto.publicationDate?.toISO(),
        unpublishedAt: dto.removalDate?.toISO(),
        status: dto.status,
      });
    } else {
      contentType = "application/x-www-form-urlencoded";
      body = new FormData();
      body.append("url", dto.url);
      body.append("imageFile", dto.image);
      body.append("headline", dto.headline);
      body.append(
        "triggersNotification",
        dto.triggersNotification ? "true" : ""
      );
      body.append("publishedAt", dto.publicationDate!.toISO());
      body.append("title", dto.title);
      body.append("status", dto.status);
      dto.removalDate && body.append("unpublishedAt", dto.removalDate.toISO());
    }

    let init: RequestInit = { method, body };
    dto.image === undefined
      ? (init.headers = { "Content-Type": contentType })
      : false;

    return this.fetch(endPointUrl, init)
      .then(handleErrors)
      .then((data) => mapExternalResourceFromApi(data))
      .catch((e: Error) =>
        Promise.reject(JSON.parse(e.message)["hydra:description"])
      );
  }
}
